// Setup Tour — server-side progress persistence.
//
// HARD RULE: tour progress is stored SERVER-SIDE ONLY (no localStorage)
// so it resumes on any device. We reuse the app's canonical user_state
// contract via cloudBackup: one row per (user_id, key) holding a JSON
// blob. cloudSet() is fire-and-forget and also updates the in-memory
// snapshot, so a same-session re-read sees the latest value.

'use strict';

import { supabase } from '../../lib/supabase';
import { cloudSet, snapshotGet, snapshotIsReady } from '../../lib/cloudBackup';
import { mergeTourDefaults, DEFAULT_TOUR_STATE } from './tourLogic';

export const TOUR_STATE_KEY = 'setup_tour_v1';

// Read the tour blob for a user. Prefer the already-loaded in-memory
// snapshot (populated at sign-in); fall back to a direct one-key query so
// the tour still works if it mounts before the snapshot is warm. Always
// resolves to a well-formed state (never throws).
export async function loadTourState(userId) {
  if (!userId) return mergeTourDefaults(null);
  try {
    if (snapshotIsReady(userId)) {
      return mergeTourDefaults(snapshotGet(userId, TOUR_STATE_KEY));
    }
    const { data, error } = await supabase
      .from('user_state')
      .select('value')
      .eq('user_id', userId)
      .eq('key', TOUR_STATE_KEY)
      .maybeSingle();
    if (error) return mergeTourDefaults(null);
    return mergeTourDefaults(data && data.value);
  } catch {
    return mergeTourDefaults(null);
  }
}

// Persist the tour blob. Fire-and-forget (matches cloudSet semantics) —
// the UI never blocks on the write. Returns the value that was written so
// callers can keep local React state in sync.
export function saveTourState(userId, next) {
  const value = mergeTourDefaults(next);
  if (userId) {
    try { cloudSet(userId, TOUR_STATE_KEY, value); } catch { /* best-effort */ }
  }
  return value;
}

export { DEFAULT_TOUR_STATE };
